/**
 * Granuláty calculator — two modes feeding one enquiry form:
 *
 * - exchange (default): scrap weight × purity = fine metal, minus the
 *   exchange fee, gives the granulate handed back. No live price needed.
 * - purchase: weight in, price out (spot + margin).
 *
 * The form lives in its own section below the calculator. Its hidden fields
 * are kept in sync so a submission carries the figures the customer actually
 * saw — but only once they have used the calculator (the page opens on the
 * client's example numbers, which must not reach the e-mail) and only until
 * they remove the summary shown above the form. Only the active mode's fields
 * are enabled, so the e-mail never mixes the two.
 *
 * The unit price is already rounded by granulaty-price.js; we multiply
 * that rounded number, so unit × weight always equals the shown total.
 */
(function () {
  'use strict';

  var METAL_LABELS = { gold: 'Zlato', silver: 'Stříbro' };
  var MODE_LABELS = { exchange: 'Výměna za granulát', purchase: 'Nákup granulátu' };
  var FINE_LABELS = { gold: 'Obsah ryzího zlata', silver: 'Obsah ryzího stříbra' };
  var SCRAP_LABELS = { gold: 'zlomkového zlata', silver: 'zlomkového stříbra' };
  // The client's own worked examples — what the page shows on arrival.
  var EXCHANGE_DEFAULTS = {
    gold: { weight: '10', purity: '585' },
    silver: { weight: '100', purity: '900' }
  };
  var STORAGE_KEY = 'avarrio_granulaty_calc';

  function init() {
    var root = document.querySelector('[data-granulaty-calculator]');
    var form = document.getElementById('GranulatyForm');
    if (!root || !form) return;

    var api = window.avarrio && window.avarrio.granulaty;
    if (!api) return;

    var weightInput = root.querySelector('[data-granulaty-weight]');
    var totalEl = root.querySelector('[data-granulaty-total]');
    var totalVatEl = root.querySelector('[data-granulaty-total-vat]');
    var unitReadout = root.querySelector('[data-granulaty-unit-readout]');
    var metalTabs = root.querySelectorAll('[data-granulaty-metal]');
    var unitButtons = root.querySelectorAll('[data-granulaty-unit]');
    var presets = root.querySelectorAll('[data-granulaty-preset]');
    var modeTabs = root.querySelectorAll('[data-granulaty-mode]');
    var panels = root.querySelectorAll('[data-granulaty-panel]');
    var calcFields = form.querySelectorAll('[data-granulaty-calc-field]');
    var summary = document.querySelector('[data-granulaty-summary]');
    var summaryText = document.querySelector('[data-granulaty-summary-text]');
    var attachButton = root.querySelector('[data-granulaty-attach]');
    var detachButton = document.querySelector('[data-granulaty-detach]');
    var scrapInput = root.querySelector('[data-granulaty-scrap-weight]');
    var purityInput = root.querySelector('[data-granulaty-purity]');
    var purityPresets = root.querySelectorAll('[data-granulaty-purity-preset]');
    var exOutEl = root.querySelector('[data-granulaty-ex-out]');
    var exFineEl = root.querySelector('[data-granulaty-ex-fine]');
    var exFineLabelEl = root.querySelector('[data-granulaty-ex-fine-label]');
    var scrapLabelEl = root.querySelector('[data-granulaty-scrap-label]');
    var purityLabelEl = root.querySelector('[data-granulaty-purity-label]');
    var exFeeEl = root.querySelector('[data-granulaty-ex-fee]');
    var exFeePctEl = root.querySelector('[data-granulaty-ex-fee-pct]');

    // Without these the calculator has nothing to render into — bail rather
    // than throw on every keystroke if a data- hook ever gets renamed.
    if (!weightInput || !totalEl || !totalVatEl || !unitReadout) return;
    if (!scrapInput || !purityInput || !exOutEl || !exFineEl || !exFeeEl) return;

    var fees = {
      gold: parseFloat(root.getAttribute('data-fee-gold-pct')),
      silver: parseFloat(root.getAttribute('data-fee-silver-pct'))
    };
    if (!isFinite(fees.gold)) fees.gold = 2;
    if (!isFinite(fees.silver)) fees.silver = 10;

    var mode = 'exchange';
    var metal = 'gold';
    var unit = 'g';
    // Whether the calculation goes out with the enquiry — see the header.
    var attached = false;
    // Scrap weight and purity are remembered per metal: switching tabs must
    // not carry a 585 gold purity over to silver.
    var exchange = {
      gold: { weight: EXCHANGE_DEFAULTS.gold.weight, purity: EXCHANGE_DEFAULTS.gold.purity },
      silver: { weight: EXCHANGE_DEFAULTS.silver.weight, purity: EXCHANGE_DEFAULTS.silver.purity }
    };

    /** A missing hidden field degrades to a no-op sink, not a crash. */
    function field(name) {
      var el = form.querySelector('[data-granulaty-field="' + name + '"]');
      return el || { value: '' };
    }

    function readStoredState() {
      try {
        var raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    function writeStoredState() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          mode: mode,
          metal: metal,
          unit: unit,
          weight: weightInput.value,
          exchange: exchange,
          attached: attached
        }));
      } catch (e) {
        // Privacy mode or storage disabled — the calculator still works,
        // it just won't survive a validation-error round trip.
      }
    }

    /** Reapplies weight/metal/unit saved before a full-page form POST. */
    function restoreState() {
      var stored = readStoredState();
      if (!stored) return;

      if (stored.mode === 'exchange' || stored.mode === 'purchase') mode = stored.mode;
      if (stored.metal === 'gold' || stored.metal === 'silver') metal = stored.metal;
      if (stored.unit === 'g' || stored.unit === 'kg') unit = stored.unit;
      if (typeof stored.weight === 'string') weightInput.value = stored.weight;
      attached = stored.attached === true;
      if (stored.exchange) {
        ['gold', 'silver'].forEach(function (m) {
          var saved = stored.exchange[m];
          if (!saved) return;
          if (typeof saved.weight === 'string') exchange[m].weight = saved.weight;
          if (typeof saved.purity === 'string') exchange[m].purity = saved.purity;
        });
      }

      unitButtons.forEach(function (b) {
        var active = b.getAttribute('data-granulaty-unit') === unit;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function clearPresetState() {
      presets.forEach(function (b) { b.setAttribute('aria-pressed', 'false'); });
    }

    function setPressed(buttons, isActive) {
      buttons.forEach(function (b) {
        var active = isActive(b);
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    /** Syncs tabs, panels, purity chips and the exchange inputs to mode/metal. */
    function applyModeAndMetal() {
      setPressed(modeTabs, function (b) { return b.getAttribute('data-granulaty-mode') === mode; });
      setPressed(metalTabs, function (b) { return b.getAttribute('data-granulaty-metal') === metal; });

      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-granulaty-panel') !== mode;
      });

      purityPresets.forEach(function (b) {
        b.hidden = b.getAttribute('data-granulaty-purity-metal') !== metal;
      });

      scrapInput.value = exchange[metal].weight;
      purityInput.value = exchange[metal].purity;
      if (exFineLabelEl) exFineLabelEl.textContent = FINE_LABELS[metal];
      if (scrapLabelEl) scrapLabelEl.textContent = 'Hmotnost ' + SCRAP_LABELS[metal];
      if (purityLabelEl) purityLabelEl.textContent = 'Ryzost ' + SCRAP_LABELS[metal];
      if (exFeePctEl) exFeePctEl.textContent = formatNumber(fees[metal], 2);
    }

    function syncPurityPresets() {
      var current = parseWeight(purityInput.value);
      purityPresets.forEach(function (b) {
        var match = parseFloat(b.getAttribute('data-granulaty-purity-preset')) === current;
        b.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    }

    /**
     * Czech users type "12,5" — parseFloat would silently give 12. Some also
     * type a dot as a thousands separator ("1.234,5") — read naively that
     * parses as 1.234, a silent 1000x error. A comma present means the
     * string is Czech-formatted: dots are thousands separators (dropped)
     * and the comma is the decimal point. No comma means a lone dot, if
     * present, is already the decimal point and needs no rewriting.
     */
    function parseWeight(raw) {
      var normalised = String(raw).replace(/\s/g, '');
      if (normalised.indexOf(',') !== -1) {
        normalised = normalised.replace(/\./g, '').replace(',', '.');
      }
      var value = parseFloat(normalised);
      if (!isFinite(value) || value <= 0) return 0;
      return value;
    }

    function weightInGrams() {
      var value = parseWeight(weightInput.value);
      return unit === 'kg' ? value * 1000 : value;
    }

    /**
     * Rounds the raw input weight to the precision the price is quoted at —
     * 3 decimals of grams for gold, 6 decimals of kilograms for silver —
     * and returns it in grams. This is the ONLY rounding step: the same
     * rounded value then feeds the readout, contact[Hmotnost] and the
     * total, so a customer's hand-check always ties out.
     */
    function roundToQuotedPrecision(grams) {
      if (metal === 'gold') return Math.round(grams * 1000) / 1000;
      var roundedKg = Math.round((grams / 1000) * 1e6) / 1e6;
      return roundedKg * 1000;
    }

    /** Rounds to maxDecimals, trims trailing zeros, and groups thousands like formatCzk. */
    function formatNumber(value, maxDecimals) {
      var factor = Math.pow(10, maxDecimals);
      var rounded = Math.round(value * factor) / factor;
      var parts = String(rounded).split('.');
      var intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.length > 1 ? intPart + ',' + parts[1] : intPart;
    }

    /**
     * The weight is always shown (and recorded) in the unit the price is
     * quoted in — grams for gold, kilograms for silver — regardless of
     * which unit the customer typed in. That is the only way the readout
     * and contact[Hmotnost] can be multiplied out by hand against the
     * displayed unit price.
     */
    function displayWeight(grams) {
      return metal === 'gold'
        ? formatNumber(grams, 3) + ' g'
        : formatNumber(grams / 1000, 6) + ' kg';
    }

    function clear() {
      totalEl.textContent = '—';
      totalVatEl.textContent = '—';
      unitReadout.textContent = '';
      field('weight').value = '';
      field('total').value = '';
      field('unit').value = '';
      field('spot').value = '';
    }

    function clearExchange() {
      exOutEl.textContent = '—';
      exFineEl.textContent = '—';
      exFeeEl.textContent = '—';
      field('scrap-weight').value = '';
      field('purity').value = '';
      field('fine').value = '';
      field('granulate').value = '';
    }

    /**
     * Fine metal is rounded to 0,01 g first and the fee taken from that
     * rounded figure, so "fine − fee = granulate" ties out on screen exactly
     * like the client's own examples (5,85 g − 2 % = 5,73 g).
     */
    function updateExchange() {
      var grams = parseWeight(scrapInput.value);
      var purity = parseWeight(purityInput.value);
      syncPurityPresets();

      if (grams <= 0 || purity <= 0 || purity > 1000) {
        clearExchange();
        if (grams > 0) field('scrap-weight').value = formatNumber(grams, 3) + ' g';
        return;
      }

      var fine = Math.round(grams * purity / 1000 * 100) / 100;
      var out = Math.round(fine * (100 - fees[metal])) / 100;
      var fee = Math.round((fine - out) * 100) / 100;

      exOutEl.textContent = formatNumber(out, 2);
      exFineEl.textContent = formatNumber(fine, 2);
      exFeeEl.textContent = formatNumber(fee, 2);

      field('scrap-weight').value = formatNumber(grams, 3) + ' g';
      field('purity').value = formatNumber(purity, 1) + '/1000';
      field('fine').value = formatNumber(fine, 2) + ' g';
      field('granulate').value = formatNumber(out, 2) + ' g (poplatek ' + formatNumber(fees[metal], 2) + ' %)';
    }

    /**
     * Disabled inputs are left out of the POST entirely: nothing goes out
     * until the calculation is attached, then only the active mode's fields.
     */
    function applyAttachment() {
      calcFields.forEach(function (input) {
        var fieldMode = input.getAttribute('data-granulaty-mode-field');
        input.disabled = !attached || (fieldMode !== null && fieldMode !== mode);
      });

      if (!summary || !summaryText) return;
      var text = '';
      if (mode === 'exchange' && field('granulate').value) {
        text = 'Výměna · ' + METAL_LABELS[metal] + ' · ' + field('scrap-weight').value +
          ', ' + field('purity').value + ' → ' + exOutEl.textContent + ' g granulátu';
      } else if (mode === 'purchase' && field('total').value) {
        text = 'Nákup · ' + METAL_LABELS[metal] + ' · ' + field('weight').value +
          ' → ' + field('total').value + ' bez DPH';
      }
      summaryText.textContent = text;
      summary.hidden = !attached || !text;
    }

    /** Any deliberate use of the calculator attaches it to the enquiry. */
    function markUsed() {
      attached = true;
    }

    function update() {
      field('type').value = MODE_LABELS[mode];
      field('metal').value = METAL_LABELS[metal];

      exchange[metal].weight = scrapInput.value;
      exchange[metal].purity = purityInput.value;
      writeStoredState();

      updateExchange();

      var state = api.state;
      var grams = roundToQuotedPrecision(weightInGrams());

      if (!state.available || grams <= 0) {
        clear();
        if (grams > 0) field('weight').value = displayWeight(grams);
        applyAttachment();
        return;
      }

      var unitPrice, unitLabel, total;

      if (metal === 'gold') {
        unitPrice = state.gold.perGram;
        unitLabel = api.formatCzk(unitPrice) + ' Kč/g';
        total = Math.round(unitPrice * grams);
      } else {
        unitPrice = state.silver.perKilogram;
        unitLabel = api.formatCzk(unitPrice) + ' Kč/kg';
        total = Math.round((unitPrice * grams) / 1000);
      }

      totalEl.textContent = api.formatCzk(total);
      totalVatEl.textContent = api.formatCzk(api.withVat(total, api.vatPct));
      unitReadout.textContent = displayWeight(grams) + ' × ' + unitLabel;

      field('weight').value = displayWeight(grams);
      field('total').value = api.formatCzk(total) + ' Kč';
      field('unit').value = unitLabel;
      // Emailed, possibly read hours or days later — always carry the date,
      // unlike the on-page timestamp lines which only show it when it isn't today.
      field('spot').value = api.formatCzk(state[metal].spotPerOunce) + ' Kč/oz' +
        (state.updatedAt ? ' (' + api.formatUpdatedAtWithDate(state.updatedAt) + ')' : '');
      applyAttachment();
    }

    metalTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        metal = this.getAttribute('data-granulaty-metal');
        markUsed();
        applyModeAndMetal();
        update();
      });
    });

    modeTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        mode = this.getAttribute('data-granulaty-mode');
        markUsed();
        applyModeAndMetal();
        update();
      });
    });

    purityPresets.forEach(function (button) {
      button.addEventListener('click', function () {
        purityInput.value = this.getAttribute('data-granulaty-purity-preset');
        markUsed();
        update();
      });
    });

    function onUserInput() {
      markUsed();
      update();
    }

    scrapInput.addEventListener('input', onUserInput);
    purityInput.addEventListener('input', onUserInput);

    unitButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        unit = this.getAttribute('data-granulaty-unit');
        unitButtons.forEach(function (b) {
          var active = b === button;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        clearPresetState();
        markUsed();
        update();
      });
    });

    presets.forEach(function (button) {
      button.addEventListener('click', function () {
        var grams = parseFloat(this.getAttribute('data-granulaty-preset'));
        unit = 'g';
        unitButtons.forEach(function (b) {
          var active = b.getAttribute('data-granulaty-unit') === 'g';
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        weightInput.value = String(grams);
        presets.forEach(function (b) { b.setAttribute('aria-pressed', b === button ? 'true' : 'false'); });
        markUsed();
        update();
      });
    });

    weightInput.addEventListener('input', function () {
      clearPresetState();
      markUsed();
      update();
    });

    // The anchor's own jump to the form still happens; this only attaches.
    if (attachButton) {
      attachButton.addEventListener('click', function () {
        markUsed();
        update();
      });
    }

    if (detachButton) {
      detachButton.addEventListener('click', function () {
        attached = false;
        update();
      });
    }

    if (typeof subscribe === 'function') {
      subscribe('granulaty:price-update', update);
    } else {
      // Without pubsub the calculator never hears about price refreshes and
      // the total silently freezes — worth a loud warning, not a silent no-op.
      console.warn('avarrio: pubsub.js not found — granulaty calculator will not refresh on price updates');
    }

    // A successful submission means Shopify has already sent the poptávka;
    // any stored weight/metal/unit is a stale quote and must not resurface
    // on the next visit to this page in the same session.
    var postedSuccessfully = form.querySelector('[data-granulaty-posted-successfully]');
    if (postedSuccessfully && postedSuccessfully.getAttribute('data-granulaty-posted-successfully') === 'true') {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // Privacy mode or storage disabled — nothing to clear.
      }
    } else {
      restoreState();
      // Start on the smallest preset so the page shows a real total on arrival
      // instead of an em dash the visitor has to earn.
      if (!weightInput.value) {
        weightInput.value = '10';
        presets.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-granulaty-preset') === '10' ? 'true' : 'false');
        });
      }
    }

    applyModeAndMetal();
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
